import React, { useState, useEffect, createContext } from 'react';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AppLayout from './components/Layout/AppLayout';
import LoginScreen from './components/Auth/LoginScreen';
import RegisterScreen from './components/Auth/RegisterScreen';
import AdminApp from './admin/AdminApp';
import DriverApp from './driver/DriverApp';
import LiveWindow from './components/Routes/LiveWindow';
import TrackingPage from './components/Tracking/TrackingPage';
import store from './store';
import { applyClientDefaultOrigin } from './store/slices/uiSlice';
import theme from './theme';
import { CircularProgress, Box } from '@material-ui/core';

const PLANNER_API = '/planner-api';

// Parse URLs no formato #/track/<routeId>?t=<token>
// Devolve { routeId, token } ou null se o hash não corresponder.
function parseTrackHash(hash) {
  const m = String(hash || '').match(/^#\/track\/(\d+)(?:\?(.*))?$/);
  if (!m) return null;
  const routeId = parseInt(m[1]);
  const qs = new URLSearchParams(m[2] || '');
  const token = qs.get('t');
  if (!routeId || !token) return null;
  return { routeId, token };
}

// Context to share JWT token with Redux thunks and components
export const AuthContext = createContext({ token: null, modules: [], clientSettings: {}, clientType: null, clientName: null, clientExternalId: null, userEmail: null, userRole: null, userPermission: 'manager', updateClientSettings: () => {} });

function App() {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState(null); // 'api' or 'local'
  const [token, setToken] = useState(null);
  const [modules, setModules] = useState([]);
  const [clientSettings, setClientSettings] = useState({});
  const [clientType, setClientType] = useState(null);
  const [clientExternalId, setClientExternalId] = useState(null);
  const [screen, setScreen] = useState('loading');
  const [isAdmin, setIsAdmin] = useState(window.location.hash.startsWith('#/admin'));
  const [isDriver, setIsDriver] = useState(window.location.hash.startsWith('#/driver'));
  // Janela própria do monitor Live (#/live/<routeId>) — ver utils/liveWindow.js
  const [isLive, setIsLive] = useState(window.location.hash.startsWith('#/live'));
  // Tracking público — URL no formato #/track/<routeId>?t=<token>
  // Acedido via QR Code do RouteSummaryDialog. Não passa pelo auth flow.
  const [trackParams, setTrackParams] = useState(() => parseTrackHash(window.location.hash));

  useEffect(() => {
    function onHashChange() {
      setIsAdmin(window.location.hash.startsWith('#/admin'));
      setIsDriver(window.location.hash.startsWith('#/driver'));
      setTrackParams(parseTrackHash(window.location.hash));
      setIsLive(window.location.hash.startsWith('#/live'));
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Petrotec Angola — quando o tenant tem defaultCountry='AO' no perfil,
  // activar o modo AO automaticamente (a não ser que o operador tenha
  // tocado no toggle alguma vez, caso em que respeitamos a escolha dele).
  // Watche `clientSettings` para apanhar tanto verifyToken como login direct.
  useEffect(() => {
    const country = clientSettings?.defaultCountry;
    if (country) {
      store.dispatch(applyClientDefaultOrigin(country));
    }
  }, [clientSettings]);

  useEffect(() => {
    if (isAdmin || isDriver || isLive) return;
    const params = new URLSearchParams(window.location.search);
    const sessionToken = params.get('session');
    // Impersonation vinda do backoffice ("entrar como"): um JWT do planner já
    // emitido para o utilizador-alvo. Consome-se como uma sessão normal e limpa-se
    // já do URL (como o fluxo ?session= do EcooGPS).
    const impersonateToken = params.get('impersonate');
    if (sessionToken) {
      document.cookie = 'JSESSIONID=' + sessionToken + '; path=/';
      window.history.replaceState({}, document.title, window.location.pathname);
      loginViaProvider(sessionToken);
    } else if (impersonateToken) {
      sessionStorage.setItem('planner_token', impersonateToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      verifyToken(impersonateToken);
    } else {
      const savedToken = sessionStorage.getItem('planner_token');
      if (savedToken) {
        verifyToken(savedToken);
      } else {
        setScreen('login-planner');
      }
    }
  }, [isAdmin, isDriver, isLive]);

  async function loginViaProvider(sessionToken) {
    try {
      const res = await fetch(`${PLANNER_API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: 'ecoogps', sessionToken }),
      });
      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem('planner_token', data.token);
        setToken(data.token);
        setUser(data.user);
        setModules(data.user?.modules || data.modules || []);
        setClientSettings(data.user?.clientSettings || {});
        setClientType(data.user?.clientType || null);
        setClientExternalId(data.user?.clientExternalId || null);
        setAuthMode('api');
        setScreen('app');
        return;
      }
    } catch (err) {
      console.error('Login via provider failed:', err);
    }
    setScreen('login-planner');
  }

  async function verifyToken(savedToken) {
    try {
      const res = await fetch(`${PLANNER_API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setToken(savedToken);
        setUser(data);
        setModules(data.modules || []);
        setClientSettings(data.clientSettings || {});
        setClientType(data.clientType || null);
        setClientExternalId(data.clientExternalId || null);
        setAuthMode(data.sessionToken ? 'api' : 'local');
        setScreen('app');
        return;
      }
    } catch (err) {
      // Token invalid
    }
    sessionStorage.removeItem('planner_token');
    setScreen('login-planner');
  }

  async function handlePlannerLogin(email, password) {
    try {
      const res = await fetch(`${PLANNER_API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        sessionStorage.setItem('planner_token', data.token);
        setToken(data.token);
        setUser(data.user);
        setModules(data.user?.modules || data.modules || []);
        setClientSettings(data.user?.clientSettings || {});
        setClientType(data.user?.clientType || null);
        setClientExternalId(data.user?.clientExternalId || null);
        setAuthMode('local');
        setScreen('app');
        return { ok: true };
      }
      return { ok: false, error: data.error || 'Credenciais invalidas' };
    } catch {
      return { ok: false, error: 'Erro de ligacao ao servidor' };
    }
  }

  async function handleRegister({ companyName, name, email, password, phone }) {
    try {
      const res = await fetch(`${PLANNER_API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyName, name, email, password, phone }),
      });
      const data = await res.json();
      if (res.ok) {
        sessionStorage.setItem('planner_token', data.token);
        setToken(data.token);
        setUser(data.user);
        setModules(data.user?.modules || []);
        setClientSettings(data.user?.clientSettings || {});
        setClientType(data.user?.clientType || 'standalone');
        setClientExternalId(data.user?.clientExternalId || null);
        setAuthMode('local');
        setScreen('app');
        return { ok: true };
      }
      return { ok: false, error: data.error || 'Erro no registo' };
    } catch {
      return { ok: false, error: 'Erro de ligacao ao servidor' };
    }
  }

  // Tracking público — antes de qualquer auth/redirect, se a URL é
  // #/track/<id>?t=<token>, mostra a página standalone. Os fetches
  // internos usam o token no querystring (não precisa de JWT login).
  if (trackParams) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TrackingPage routeId={trackParams.routeId} token={trackParams.token} />
      </ThemeProvider>
    );
  }

  if (isLive) {
    return <LiveWindow />;
  }

  if (isDriver) {
    return <DriverApp />;
  }

  if (isAdmin) {
    return <AdminApp />;
  }

  if (screen === 'loading') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (screen === 'login-planner') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginScreen
          onLogin={handlePlannerLogin}
          title="Planeador de Rotas"
          subtitle="Planeamento de rotas"
          onRegister={() => setScreen('register')}
          fieldLabel="Email"
          fieldPlaceholder="Introduza o email"
        />
      </ThemeProvider>
    );
  }

  if (screen === 'register') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RegisterScreen
          onRegister={handleRegister}
          onBack={() => setScreen('login-planner')}
        />
      </ThemeProvider>
    );
  }

  return (
    <AuthContext.Provider value={{ token, modules, clientSettings, clientType, clientExternalId, clientName: user?.clientName || null, userEmail: user?.email || null, userRole: user?.role || null, userPermission: user?.permissionLevel || 'manager', updateClientSettings: setClientSettings }}>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppLayout user={user} authMode={authMode} />
          <ToastContainer
            position="bottom-right"
            autoClose={4000}
            hideProgressBar={false}
            theme="dark"
            toastStyle={{
              background: '#111827',
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#e2e8f0',
            }}
          />
        </ThemeProvider>
      </Provider>
    </AuthContext.Provider>
  );
}

export default App;
